import { Link, useParams, useNavigate, useLocation } from 'react-router-dom'
import { Loader2, CheckCircle } from 'lucide-react'
import { PropertyForm } from '@/components/properties'
import { useProperty, useUpdateProperty, useDeleteProperty } from '@/hooks'
import type { PropertyUpdateInput } from '@/hooks'
import type { PropertyUpdateData } from '@/lib/api'
import { useEffect, useState } from 'react'

/**
 * Convert form data to Convex input format.
 * Form uses `null` for empty values, Convex uses `undefined`.
 */
function formDataToConvex(data: PropertyUpdateData): PropertyUpdateInput {
  // Cast to access building-related fields that may be added by the form
  const extendedData = data as PropertyUpdateData & {
    building_id?: string
    reference_code?: string
    floor_plan_id?: string
    building_only?: boolean
  }

  return {
    title: data.title,
    slug: data.slug,
    // Descriptions - store individual fields
    description_short: data.description_short ?? undefined,
    description_long: data.description_long ?? undefined,
    // Price
    price: data.price ?? undefined,
    price_currency: data.price_currency ?? 'EUR',
    // Location - store individual fields
    address: data.address ?? undefined,
    city: data.city ?? undefined,
    province: data.province ?? undefined,
    region: data.region ?? undefined,
    postal_code: data.postal_code ?? undefined,
    // Property details
    bedrooms: data.specs?.bedrooms ?? undefined,
    bathrooms: data.specs?.bathrooms ?? undefined,
    area_m2: data.specs?.area_m2 ?? undefined,
    status: data.status,
    specs: data.specs ? {
      bedrooms: data.specs.bedrooms ?? undefined,
      bathrooms: data.specs.bathrooms ?? undefined,
      area_m2: data.specs.area_m2 ?? undefined,
      garage_spaces: data.specs.garage_spaces ?? undefined,
      year_built: data.specs.year_built ?? undefined,
      floor: data.specs.floor ?? undefined,
    } : undefined,
    // Characteristics
    characteristics: data.characteristics ? {
      pool: data.characteristics.pool ?? undefined,
      garden: data.characteristics.garden ?? undefined,
      terrace: data.characteristics.terrace ?? undefined,
      patio: (data.characteristics as any).patio ?? undefined,
      air_conditioning: data.characteristics.air_conditioning ?? undefined,
      elevator: data.characteristics.elevator ?? undefined,
      garage: (data.characteristics as any).garage ?? undefined,
      storage_room: (data.characteristics as any).storage_room ?? undefined,
      balcony: (data.characteristics as any).balcony ?? undefined,
      exterior: (data.characteristics as any).exterior ?? undefined,
      jacuzzi: (data.characteristics as any).jacuzzi ?? undefined,
      sauna: (data.characteristics as any).sauna ?? undefined,
      bbq_area: (data.characteristics as any).bbq_area ?? undefined,
      sea_views: (data.characteristics as any).sea_views ?? undefined,
      heating: data.characteristics.heating ?? undefined,
      orientation: data.characteristics.orientation ?? undefined,
      views: data.characteristics.views ?? undefined,
      energy_rating: data.characteristics.energy_rating ?? undefined,
      furnished: data.characteristics.furnished ?? undefined,
      storage: data.characteristics.storage ?? undefined,
      highlighted_features: data.characteristics.highlighted_features ?? undefined,
    } : undefined,
    featured: data.featured,
    // Badge
    badge: (data as any).badge || undefined,
    // Building relationship fields
    building_id: extendedData.building_id || undefined,
    reference_code: extendedData.reference_code || undefined,
    floor_plan_id: extendedData.floor_plan_id || undefined,
    building_only: extendedData.building_only,
  }
}

/**
 * Página de edición de propiedades - carga datos de propiedad existentes para editar.
 * Usa React Hook Form + Zod para validación y Convex para obtención de datos/mutaciones.
 */
export function PropertyEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  // Show success message from navigation state (e.g., after creation)
  const [successMessage, setSuccessMessage] = useState<string | null>(
    (location.state as { message?: string } | null)?.message ?? null
  )

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  // Clear location state after reading to prevent message on refresh
  useEffect(() => {
    if (location.state) {
      navigate(location.pathname, { replace: true, state: null })
    }
  }, [location.pathname, location.state, navigate])

  // Fetch property data
  const { data: property, isLoading, error } = useProperty(id)
  const updateMutation = useUpdateProperty()
  const deleteMutation = useDeleteProperty()

  async function handleSubmit(formData: PropertyUpdateData) {
    if (!id) return

    try {
      const convexData = formDataToConvex(formData)
      await updateMutation.mutateAsync({ id, data: convexData })
      // Navigate to properties list after successful update
      navigate('/properties', {
        state: { message: '¡Propiedad actualizada exitosamente!' },
      })
    } catch (err) {
      // Error is already captured in updateMutation.error
      console.error('Failed to update property:', err)
    }
  }

  async function handleDelete() {
    if (!id) return

    try {
      await deleteMutation.mutateAsync(id)
      navigate('/properties', {
        state: { message: 'Propiedad eliminada exitosamente.' },
      })
    } catch (err) {
      // Error is already captured in deleteMutation.error
      console.error('Failed to delete property:', err)
    }
  }

  // Estado de carga
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Cargando propiedad...</span>
        </div>
      </div>
    )
  }

  // Estado de error
  if (error) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <Link
            to="/properties"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            ← Volver a Propiedades
          </Link>
        </div>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center dark:border-red-800 dark:bg-red-900/20">
          <h2 className="text-lg font-semibold text-red-700 dark:text-red-400">
            Error al cargar la propiedad
          </h2>
          <p className="mt-2 text-sm text-red-600 dark:text-red-300">
            {error.message}
          </p>
          <Link
            to="/properties"
            className="mt-4 inline-block rounded-md bg-red-100 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
          >
            Volver a Propiedades
          </Link>
        </div>
      </div>
    )
  }

  // Estado de no encontrado
  if (!property) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <div>
          <Link
            to="/properties"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            ← Volver a Propiedades
          </Link>
        </div>
        <div className="rounded-lg border bg-muted/50 p-6 text-center">
          <h2 className="text-lg font-semibold">Propiedad no encontrada</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            La propiedad que buscas no existe o ha sido eliminada.
          </p>
          <Link
            to="/properties"
            className="mt-4 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Volver a Propiedades
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Success Message */}
      {successMessage && (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
          <CheckCircle className="h-4 w-4" />
          {successMessage}
        </div>
      )}

      {/* Encabezado */}
      <div>
        <Link
          to="/properties"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          ← Volver a Propiedades
        </Link>
        <h1 className="mt-4 text-3xl font-bold tracking-tight">Editar Propiedad</h1>
        <p className="mt-2 text-muted-foreground">
          Editando: <span className="font-medium text-foreground">{property.title}</span>
        </p>
      </div>

      {/* Form */}
      <PropertyForm
        property={property as any}
        isEdit
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        isSubmitting={updateMutation.isPending}
        isDeleting={deleteMutation.isPending}
        submitError={updateMutation.error?.message ?? deleteMutation.error?.message}
      />
    </div>
  )
}
